import React from "react";
import { toast } from "react-toastify";

/**
 * GallerySlot
 * Componente aislado para cada celda de la galería.
 * Al ser un componente propio, Tailwind escanea sus clases estáticamente
 * y no las purga en el build de producción.
 */
const GallerySlot = ({ index, imageUrl, uploading, uploadingSlot, onUpload, onDelete }) => {
  const isUploading = uploading && uploadingSlot === index;

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format not supported. Please use JPG, PNG or WebP.");
        event.target.value = "";
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB
        toast.error("File is too large. Maximum size is 10MB.");
        event.target.value = "";
        return;
      }
      onUpload(event, index);
    }
  };

  if (imageUrl) {
    return (
      <div className="relative group">
        <img
          src={imageUrl}
          alt={`Gallery item ${index + 1}`}
          className="w-full h-36 object-cover rounded-lg border border-gray-200"
        />
        <button
          type="button"
          className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
          onClick={() => onDelete(index)}
          title="Remove image"
        >
          ✕
        </button>
        <span className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-[10px] px-1.5 py-0.5 rounded">
          {index + 1}
        </span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-36 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col justify-center items-center hover:border-cyan-400 hover:bg-cyan-50 transition-colors cursor-pointer">
      {isUploading ? (
        <div className="flex flex-col items-center px-1">
          <svg
            className="animate-spin h-6 w-6 text-cyan-500 mb-1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-[10px] text-cyan-600 font-medium">Uploading...</p>
        </div>
      ) : (
        <>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/jpg"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <p className="text-center text-[10px] text-gray-500 mt-1 font-medium px-1">
            Add Image {index + 1}
          </p>
        </>
      )}
    </div>
  );
};

export default GallerySlot;
